// react
import React, { useEffect, useRef, useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
// store
import { removeAllFromBasket, emptyBasketConfiguration } from "../../store/actions";
import { authService } from "../../store/services/ServiceAuth";
import { paymentService } from "../../store/services/ServicePayment";
// components
import BasketItem from "./BasketItem";
import LoginForm from "../login/LoginForm";
import {
    Modal,
    Button,
    Segment,
    Header,
    List,
    Divider,
    Message,
    Icon,
} from "semantic-ui-react";
import RegisterClient from "../register/RegisterClient";

const REDIRECT_DELAY_MS = 1000;

function PaymentStatus({ title, url }) {
    return (
        <div style={{
            minHeight: "16.5rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "2.75rem 1.75rem 2.25rem",
        }}>
            <style>{`
                @keyframes payment-gateway-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
            <div
                style={{
                    width: "4.5rem",
                    height: "4.5rem",
                    margin: "0 auto 1.35rem",
                    borderRadius: "50%",
                    background: "rgba(214, 12, 140, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div
                    style={{
                        width: "1.7rem",
                        height: "1.7rem",
                        borderRadius: "50%",
                        border: "3px solid rgba(214, 12, 140, 0.2)",
                        borderTopColor: "var(--primary, #d60c8c)",
                        animation: "payment-gateway-spin 0.8s linear infinite",
                    }}
                />
            </div>
            <p style={{
                margin: "0 auto 1.35rem",
                color: "var(--dark)",
                fontSize: "1.2rem",
                fontWeight: 600,
                lineHeight: 1.45,
                whiteSpace: "nowrap",
            }}>
                {title}
            </p>
            <a
                href={url || undefined}
                tabIndex={url ? 0 : -1}
                style={{
                    visibility: url ? "visible" : "hidden",
                    color: "var(--primary, #d60c8c)",
                    fontWeight: 700,
                    textDecoration: "none",
                    borderBottom: "1px solid currentColor",
                }}
            >
                Ak sa brána neotvorí, pokračujte na platbu
            </a>
        </div>
    );
}

// component
const PaymentModal = ({
    trigger,
    isOpen,
    onClose,
    onOpen,
    basket_items,
    removeItem,
    setBasketEvents,
    items,
    setItems
}) => {
    // state
    const [processing, setProcessing] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [paymentURL, setPaymentURL] = useState("");
    const [isConflict, setIsConflict] = useState(false);
    const [isWrongUserLoggedIn, setIsWrongUserLoggedIn] = useState(false);
    const [isClientAccount, setIsClientAccount] = useState(false);
    const [isRegisterRequested, setIsRegisterRequested] = useState(false);
    const [registerStep, setRegisterStep] = useState("form");
    const [registeredEmail, setRegisteredEmail] = useState("");
    const [blocked, setBlocked] = useState([]);
    const [allowed, setAllowed] = useState([]);
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const redirectTimer = useRef(null);

    useEffect(() => {
        async function paymentInit() {
            if (isOpen) {
                // check if user is logged in:
                if (user.role && user.role === "User") {
                    if ((await authService.getAccessToken()) !== false) {
                        setIsClientAccount(true);
                        setProcessing(true);
                        setIsChecking(false);
                        await check_payment("from useEffect");
                    }
                }
                setIsChecking(false);
            }
        }

        paymentInit();
        // eslint-disable-next-line
    }, [isOpen]);

    const check_payment = async (text) => {
        // reset to initial
        setIsConflict(false);
        setBlocked([]);
        setAllowed([]);
        setProcessing(true);

        // call API endpoint:
        const result = await paymentService.getPaymentInitForEvent({
            events: basket_items.events,
            future_events: basket_items.future_events,
            basket_config: items
        });

        if (result.status === 200 && result.response && result.response.payment_url) {
            const url = result.response.payment_url;
            setPaymentURL(url);
            setProcessing(false);
            redirectTimer.current = window.setTimeout(() => {
                window.location.href = url;
            }, REDIRECT_DELAY_MS);
            return;
        } else if (result.status === 409) {
            setBlocked(result.response.blocked);
            setAllowed(result.response.allowed);
            setIsConflict(true);
        }

        setProcessing(false);
    };

    const onLogin = async (status) => {
        if (status !== undefined && status === "wrong_user") {
            setIsWrongUserLoggedIn(true)
        } else {
            setIsWrongUserLoggedIn(false)
            setIsClientAccount(true);
            await check_payment();
        }
    };

    const removeItemFromView = (item) => {
        setBlocked(blocked.filter((blocked) => blocked !== item));
    };

    const cancelOrder = () => {
        dispatch(removeAllFromBasket());
        dispatch(emptyBasketConfiguration());
        setBasketEvents([]);
        setItems([]);
        onClose();
    };

    const setLoginForm = () => {
        setIsRegisterRequested(false);
        setRegisterStep("form");
        setRegisteredEmail("");
    };

    const closeModal = () => {
        if (redirectTimer.current) {
            window.clearTimeout(redirectTimer.current);
            redirectTimer.current = null;
        }
        setIsRegisterRequested(false);
        setRegisterStep("form");
        setRegisteredEmail("");
        onClose();
    };

    const handleRegisterStep = (step, emailAddress) => {
        setRegisterStep(step);
        if (emailAddress) {
            setRegisteredEmail(emailAddress);
        }
    };

    const registerHeading = {
        form: ["Registrácia", "Pred pokračovaním si vytvorte účet."],
        code: ["Aktivácia", "Vlož kód, ktorý sme poslali na e-mail."],
        verified: ["Účet je overený", "Môžeš sa prihlásiť."],
    }[registerStep];

    // template
    return (
        <Modal
            closeOnDimmerClick={false}
            closeOnDocumentClick={false}
            closeOnEscape={false}
            dimmer
            centered={false}
            size={isConflict ? "large" : "tiny"}
            onClose={closeModal}
            onOpen={onOpen}
            open={isOpen}
            trigger={trigger}
            closeIcon={true}
        >
            <Segment style={{ background: 'none', boxShadow: 'none', border: 'none' }}>
                <Modal.Content style={{ padding: isConflict ? "2rem" : 0 }}>
                    {paymentURL ? (
                        <PaymentStatus title="Prebieha presmerovanie…" url={paymentURL} />
                    ) : processing ? (
                        <PaymentStatus title="Spracovávam objednávku…" />
                    ) : isChecking ? (
                        <PaymentStatus title="Kontrolujem účet…" />
                    ) : (
                    <Fragment>
                        <Fragment>
                            {!isClientAccount && (
                                isRegisterRequested ? (
                                    <div style={{ padding: "1rem 0" }}>
                                        <Header
                                            as="h2"
                                            style={{
                                                textAlign: "center",
                                                marginBottom: "0.75rem",
                                                color: "var(--dark)",
                                                fontSize: "1.5rem",
                                                fontWeight: "600",
                                            }}
                                        >
                                            {registerHeading[0]}
                                        </Header>
                                        <p style={{
                                            textAlign: "center",
                                            fontSize: "1rem",
                                            color: "#666",
                                            marginBottom: "2rem",
                                            lineHeight: "1.6",
                                        }}>
                                            {registerHeading[1]}
                                        </p>
                                        {registerStep === "verified" ? (
                                            <LoginForm
                                                onLogin={onLogin}
                                                basketRegistration
                                                passwordOnly
                                                initialEmail={registeredEmail}
                                            />
                                        ) : (
                                            <RegisterClient
                                                basketRegistration={true}
                                                setLoginForm={setLoginForm}
                                                onRegisterStep={handleRegisterStep}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ padding: "1rem 0" }}>
                                        <Header
                                            as="h2"
                                            style={{
                                                textAlign: "center",
                                                marginBottom: "0.75rem",
                                                color: "var(--dark)",
                                                fontSize: "1.5rem",
                                                fontWeight: "600",
                                            }}
                                        >
                                            Prihlásenie
                                        </Header>
                                        <p style={{
                                            textAlign: "center",
                                            fontSize: "1rem",
                                            color: "#666",
                                            marginBottom: "2rem",
                                            lineHeight: "1.6",
                                        }}>
                                            Pred pokračovaním sa musíte prihlásiť.
                                        </p>
                                        <LoginForm
                                            onLogin={onLogin}
                                            onRegister={() => setIsRegisterRequested(true)}
                                            basketRegistration={true}
                                        />
                                        {isWrongUserLoggedIn && (
                                            <Message
                                                negative
                                                style={{
                                                    textAlign: "center",
                                                    marginTop: "1.5rem",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <Icon name="warning circle" />
                                                <Message.Content>
                                                    <Message.Header>Neplatný účet</Message.Header>
                                                    <p>Užívateľský účet, ktorý ste zadali nie je platným užívateľským účtom. Prosím prihláste sa na Váš užívateľský účet.</p>
                                                </Message.Content>
                                            </Message>
                                        )}
                                    </div>
                                )
                            )}
                        </Fragment>

                    {isConflict && (
                        <Fragment>
                            <Header
                                as="h4"
                                textAlign="center"
                                style={{ marginTop: 0 }}
                            >
                                Ľutujeme, objednávku nebolo možné spracovať,
                                nižšie uvedené termíny nie je možné objednať.{" "}
                                <br />
                                Odoberte položky pre pokračovanie, alebo zrušte
                                proces objednávky.
                            </Header>
                            {blocked.length > 0 && (
                                <>
                                    <Divider />
                                    <List divided verticalAlign="middle">
                                        {blocked.map((item, index) => (
                                            <BasketItem
                                                index={index}
                                                key={index}
                                                item={item}
                                                items={items}
                                                setItems={setItems}
                                                removeProductFromBasket={removeItem}
                                                removeItemFromView={
                                                    removeItemFromView
                                                }
                                            />
                                        ))}
                                    </List>
                                </>
                            )}
                            {allowed.length === 0 && (
                                <Message
                                    style={{
                                        textAlign: "center",
                                        fontWeight: "bold",
                                    }}
                                    negative
                                >
                                    Neexistuje žiadna povolená položka na
                                    pokračovanie v objednávke.
                                </Message>
                            )}
                            <Button.Group fluid size="huge">
                                <Button onClick={() => cancelOrder()} negative>
                                    Zrušiť objednávku
                                </Button>
                                {allowed.length !== 0 && (
                                    <Button
                                        onClick={() => check_payment()}
                                        positive
                                        disabled={
                                            (isConflict &&
                                                blocked.length > 0) ||
                                            allowed.length === 0
                                        }
                                    >
                                        Pokračovať ( {allowed.length} )
                                    </Button>
                                )}
                            </Button.Group>
                        </Fragment>
                    )}
                    </Fragment>
                    )}
                </Modal.Content>
            </Segment>
        </Modal>
    );
};

export default PaymentModal;
